import template from "./quote-header-modal.html";
import padStart from "lodash/padStart";

const QuoteHeaderModalComponent = {
  bindings: {
    resolve: "<",
    close: "&",
    dismiss: "&",
  },

  template: template,

  controller: class QuoteHeaderModalController {
    /* @ngInject */
    constructor($rootScope, $scope, Quote, ModalsManager, Contact, SecurityManager, quoteLanguages) {
      this.$rootScope = $rootScope;
      this.$scope = $scope;
      this.Quote = Quote;
      this.ModalsManager = ModalsManager;
      this.Contact = Contact;
      this.SecurityManager = SecurityManager;
      this.quoteLanguages = quoteLanguages;
    }

    $onInit() {
      const defaultQuoteNumber = this.$rootScope.organization.getDefaultQuoteNumber();
      this.quoteNumberPrefix = defaultQuoteNumber.prefix;
      this.quoteNumber = Number(defaultQuoteNumber.nextNumber);
      this.originalValue = Number(defaultQuoteNumber.nextNumber);
      this.openedMoreData = false;
      this.errorCodeIsNotUnique = false;
      this.quoteNumbers = this.$rootScope.organization.settings.quoteNumbers;
      this.quote = null;
      this.isEdit = this.resolve.params.edit;
      this.saving = false;
      this.contacts = [];

      if (this.isEdit) {
        this.quote = this.resolve.params.quote;
      } else {
        this.template = null;
        this.quoteToClone = null;
        this.quote = new this.Quote();

        if (this.resolve.params && this.resolve.params.template && this.resolve.params.name) {
          this.isDefaultTemplate = this.resolve.params.isDefaultTemplate;
          this.isImportedTemplate = this.resolve.params.isImportedTemplate;
          this.quote.description = this.resolve.params.name;
          this.quote.language = this.resolve.params.language;
          this.quote.lastVersion.currency = this.resolve.params.currency;
          this.template = this.resolve.params.template;
        }

        if (this.resolve.params && this.resolve.params.quoteToClone) {
          this.quoteToClone = this.resolve.params.quoteToClone;
          this.quote.description = this.resolve.params.name;
          this.quote.language = this.resolve.params.language;
          this.quote.lastVersion.currency = this.resolve.params.currency;

          const prefix = this.resolve.params.prefix;
          this.quoteNumberPrefix = prefix || "";
          if (prefix) {
            if (this.quoteNumbers.some((quoteNumber) => quoteNumber.prefix === prefix)) {
              this.quoteNumbers.forEach((quoteNumber) => {
                if (quoteNumber.prefix === prefix) this.quoteNumber = Number(quoteNumber.nextNumber);
                this.originalValue = Number(quoteNumber.nextNumber);
              });
            } else {
              this.quoteNumber = 1;
              this.originalValue = 1;
            }
          } else {
            this.quoteNumbers.forEach((quoteNumber) => {
              if (!quoteNumber.prefix) {
                this.quoteNumber = Number(quoteNumber.nextNumber);
                this.originalValue = Number(quoteNumber.nextNumber);
              }
            });
          }
        }
      }

      this.$scope.$watch(
        () => this.quoteNumberPrefix,
        (newValue, oldValue) => {
          if (!angular.equals(newValue, oldValue)) {
            if (this.quoteNumbers.some((quoteNumber) => quoteNumber.prefix === newValue)) {
              this.quoteNumbers.forEach((quoteNumber) => {
                if (quoteNumber.prefix === newValue) {
                  this.quoteNumber = Number(quoteNumber.nextNumber);
                  this.originalValue = Number(quoteNumber.nextNumber);
                }
              });
            } else {
              this.quoteNumber = 1;
              this.originalValue = 1;
            }
          }
        },
      );

      if (!this.quote.lastVersion.currency) this.setCurrency(this.$rootScope.organization.settings.currency);
      this.oldCurrency = this.quote.lastVersion.currency;
    }

    close() {
      return this.dismiss({ $value: "cancel" });
    }

    showMoreData() {
      this.openedMoreData = !this.openedMoreData;
    }

    setCurrency(oldCurrency, newCurrency) {
      if (!newCurrency) this.quote.lastVersion.currency = oldCurrency;
      else this.quote.lastVersion.currency = newCurrency;
    }

    setLanguage(oldQuoteLanguage, newQuoteLanguage) {
      if (!newQuoteLanguage) this.quote.language = oldQuoteLanguage;
      else this.quote.language = newQuoteLanguage;
    }

    setQuoteNumber(quoteNumber) {
      this.quoteNumberPrefix = quoteNumber.prefix;
    }

    async createContact() {
      const newContact = await this.ModalsManager.openContactModal(new this.Contact(), false);
      this.quote.contact = newContact || this.quote.contact;
      await this.refreshContacts();
      this.$rootScope.$applyAsync();
    }

    onChangeContact() {
      if (!this.quote._id) this.setCurrency(this.quote.contact.currency);
    }

    async refreshContacts(searchText) {
      this.contacts = await this.Contact.query({ q: searchText, limit: 50 });
      this.$rootScope.$applyAsync();
    }

    toggleSignatureToAccept() {
      if (this.quote.lastVersion.signature && this.quote.lastVersion.signature.requiredToAccept) {
        this.quote.lastVersion.signature.requiredToAccept = false;
        this.quote.lastVersion.signature.snapshot = undefined;
      } else {
        this.quote.lastVersion.signature = {
          requiredToAccept: true,
          snapshot: this.$rootScope.organization.settings.signature,
        };
      }
    }

    toggleEmailToCustomerOnAccept() {
      this.quote.emailToCustomerOnAccept = !this.quote.emailToCustomerOnAccept;
    }

    togglePdfDownloadable() {
      this.quote.pdfDownloadable = !this.quote.pdfDownloadable;
    }

    toggleApproveFromPublicView() {
      this.quote.approveFromPublicView = !this.quote.approveFromPublicView;
    }

    async save() {
      if (!this.form.$valid) {
        if (!this.openedMoreData) this.showMoreData();
        return;
      }

      this.saving = true;
      let quote;
      if (this.quote._id) {
        this.newCurrency = this.quote.lastVersion.currency;
        if (this.oldCurrency && this.oldCurrency !== this.newCurrency) {
          this.quote.lastVersion.updateCurrency(this.oldCurrency, this.newCurrency);
        }
        await this.quote.updateHeader();
        await this.quote.updateSections();
        quote = this.quote;
        this.saving = false;
        this.$rootScope.$applyAsync();
        this.close({ $value: quote });
      } else {
        this.quote.code = (this.quoteNumberPrefix || "") + padStart(this.quoteNumber, 4, "0");
        try {
          quote = await this.quote.create(
            this.template,
            this.isDefaultTemplate,
            this.isImportedTemplate,
            this.quoteToClone,
          );
          await this.SecurityManager.refreshUser();
          this.saving = false;
          this.$rootScope.$applyAsync();
          this.close({ $value: quote });
        } catch (error) {
          if (error.status === 400 && error.data === "Quote code is not unique") {
            this.errorCodeIsNotUnique = true;
            this.saving = false;
            if (!this.openedMoreData) this.showMoreData();
            this.$rootScope.$applyAsync();
          } else {
            throw error;
          }
        }
      }
    }
  },
};

export default QuoteHeaderModalComponent;
